import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Alert,
  Chip,
  Switch,
} from '@material-tailwind/react';
import Select from 'react-select';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import firebaseDb from '../../firebase';

const functions = getFunctions();
if (window.location.hostname === 'localhost') {
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

const funcGetModels = httpsCallable(functions, 'getAircraftPostModels', { timeout: 120_000 });
const funcScrapeSales = httpsCallable(functions, 'scrapeAircraftPostSales', { timeout: 600_000 });
const funcGetSales = httpsCallable(functions, 'getAircraftPostSales', { timeout: 60_000 });
const funcGetRuns = httpsCallable(functions, 'getAircraftPostScrapeRuns', { timeout: 60_000 });
const funcScrapeOnMarket = httpsCallable(functions, 'scrapeAircraftPostOnMarket', { timeout: 600_000 });
const funcGetOnMarket = httpsCallable(functions, 'getAircraftPostOnMarket', { timeout: 60_000 });
const funcGetScheduleConfig = httpsCallable(functions, 'getAircraftPostScheduleConfig', { timeout: 30_000 });
const funcSaveScheduleConfig = httpsCallable(functions, 'saveAircraftPostScheduleConfig', { timeout: 30_000 });

const CHANGE_RENDERER = (params) => {
  const count = params.value;
  if (!count) return '';
  return (
    <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
      {count} edit{count > 1 ? 's' : ''}
    </span>
  );
};

const DATE_FORMATTER = (params) =>
  params.value ? new Date(params.value).toLocaleString() : '';

export default function AircraftPostSalesPage() {
  // --- mode: sales vs on-market ---
  const [mode, setMode] = useState('sales');

  // --- shared state ---
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModels, setSelectedModels] = useState([]);
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [error, setError] = useState(null);
  const [filterModel, setFilterModel] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('scrape');

  // --- sales state ---
  const [period, setPeriod] = useState(12);
  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);

  // --- on-market state ---
  const [onMarketState, setOnMarketState] = useState('for_sale');
  const [onMarketListings, setOnMarketListings] = useState([]);
  const [onMarketLoading, setOnMarketLoading] = useState(false);

  // --- load models on mount ---
  useEffect(() => {
    loadModels();
  }, []);

  const loadModels = async () => {
    setModelsLoading(true);
    try {
      const cacheDoc = await getDoc(doc(firebaseDb, 'aircraftpost_config', 'models_cache'));
      if (cacheDoc.exists()) {
        setModels(cacheDoc.data().models || []);
      } else {
        const res = await funcGetModels({ refresh: true });
        if (res.data.success) {
          setModels(res.data.models);
        } else {
          setError(res.data.error);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setModelsLoading(false);
    }
  };

  const modelOptions = useMemo(() => {
    const grouped = {};
    for (const m of models) {
      const mfr = m.manufacturer || 'Other';
      if (!grouped[mfr]) grouped[mfr] = [];
      grouped[mfr].push({ value: m.value, label: m.text });
    }
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mfr, options]) => ({ label: mfr, options }));
  }, [models]);

  // --- scrape handler ---
  const handleScrape = async () => {
    if (selectedModels.length === 0) return;
    setScraping(true);
    setError(null);
    setScrapeResult(null);

    const makeModelIds = selectedModels.map((m) => m.value);
    const makeModelMap = {};
    selectedModels.forEach((m) => { makeModelMap[m.value] = m.label; });

    try {
      const func = mode === 'sales' ? funcScrapeSales : funcScrapeOnMarket;
      const params = mode === 'sales'
        ? { makeModelIds, period, makeModelMap }
        : { makeModelIds, makeModelMap, state: onMarketState };

      const res = await func(params);
      if (res.data.success) {
        setScrapeResult(res.data);
        if (mode === 'sales') loadSales();
        else loadOnMarket();
        loadRuns();
      } else {
        setError(res.data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setScraping(false);
    }
  };

  // --- load sales ---
  const loadSales = useCallback(async () => {
    setSalesLoading(true);
    try {
      const res = await funcGetSales({ makeModelId: filterModel?.value || null, limit: 500 });
      if (res.data.success) setSales(res.data.sales);
    } catch (err) {
      console.error('Failed to load sales:', err);
    } finally {
      setSalesLoading(false);
    }
  }, [filterModel]);

  // --- load on-market ---
  const loadOnMarket = useCallback(async () => {
    setOnMarketLoading(true);
    try {
      const res = await funcGetOnMarket({ makeModelId: filterModel?.value || null, limit: 500 });
      if (res.data.success) setOnMarketListings(res.data.listings);
    } catch (err) {
      console.error('Failed to load on-market:', err);
    } finally {
      setOnMarketLoading(false);
    }
  }, [filterModel]);

  useEffect(() => {
    if (activeTab === 'data') {
      if (mode === 'sales') loadSales();
      else loadOnMarket();
    }
  }, [activeTab, mode, loadSales, loadOnMarket]);

  // --- load runs ---
  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await funcGetRuns({ limit: 30 });
      if (res.data.success) setRuns(res.data.runs);
    } catch (err) {
      console.error('Failed to load runs:', err);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'runs') loadRuns();
  }, [activeTab, loadRuns]);

  // --- clear results when switching mode ---
  useEffect(() => {
    setScrapeResult(null);
    setActiveTab('scrape');
  }, [mode]);

  // --- column definitions ---
  const salesColDefs = useMemo(() => [
    { field: 'manufacturer', headerName: 'Manufacturer', width: 140, pinned: 'left' },
    { field: 'makeModel', headerName: 'Make/Model', width: 160, pinned: 'left' },
    { field: 'serialNumber', headerName: 'S/N', width: 80, pinned: 'left' },
    { field: 'Registration', width: 110 },
    { field: 'Sale Date', width: 110 },
    { field: 'Sale Price (M)', headerName: 'Sale Price', width: 110 },
    { field: 'Ask Price @ Sale(M)', headerName: 'Ask Price', width: 110 },
    { field: 'Original Ask Price (M)', headerName: 'Orig Ask', width: 110 },
    { field: 'MFR Year', headerName: 'Year', width: 70 },
    { field: 'Current Airframe Hours', headerName: 'Hours', width: 90 },
    { field: 'Current Total Landings', headerName: 'Landings', width: 90 },
    { field: 'Days On Market', headerName: 'DOM', width: 70 },
    { field: 'Engine Program Type', headerName: 'Engine Prog', width: 110 },
    { field: 'APU Program', headerName: 'APU Prog', width: 100 },
    { field: 'Base', width: 80 },
    { field: 'Country', width: 70 },
    { field: 'Prior Owners', headerName: 'Owners', width: 75 },
    { field: 'Interior (yrs)', headerName: 'Int (yrs)', width: 80 },
    { field: 'Paint (yrs)', headerName: 'Paint (yrs)', width: 85 },
    { field: 'Certification', width: 100 },
    { field: 'changeHistory', headerName: 'Changes', width: 90,
      valueGetter: (p) => (p.data.changeHistory || []).length, cellRenderer: CHANGE_RENDERER },
    { field: 'lastUpdatedAt', headerName: 'Last Updated', width: 160, valueFormatter: DATE_FORMATTER },
    { field: 'firstSeenAt', headerName: 'First Seen', width: 160, valueFormatter: DATE_FORMATTER },
    { field: 'Notes', width: 300, tooltipField: 'Notes' },
  ], []);

  const onMarketColDefs = useMemo(() => [
    { field: 'manufacturer', headerName: 'Manufacturer', width: 140, pinned: 'left' },
    { field: 'makeModel', headerName: 'Make/Model', width: 160, pinned: 'left' },
    { field: 'serialNumber', headerName: 'S/N', width: 80, pinned: 'left' },
    { field: 'Registration', width: 110 },
    { field: 'Ask Price (M)', headerName: 'Ask Price', width: 110 },
    { field: 'Avg Selling Price (M)', headerName: 'Avg Sell Price', width: 120 },
    { field: 'MFR Year', headerName: 'Year', width: 70 },
    { field: 'Airframe Hours', headerName: 'Hours', width: 90 },
    { field: 'Total Landings', headerName: 'Landings', width: 90 },
    { field: 'Time on Market (days)', headerName: 'DOM', width: 70 },
    { field: 'Owner', width: 120 },
    { field: 'Broker', width: 120 },
    { field: 'Engine Program Type', headerName: 'Engine Prog', width: 110 },
    { field: 'APU Program', headerName: 'APU Prog', width: 100 },
    { field: 'Base', width: 80 },
    { field: 'Country', width: 70 },
    { field: 'Prior Owners', headerName: 'Owners', width: 75 },
    { field: 'Damage History', headerName: 'Damage', width: 90 },
    { field: 'Interior (years)', headerName: 'Int (yrs)', width: 80 },
    { field: 'Paint (years)', headerName: 'Paint (yrs)', width: 85 },
    { field: 'Certification', width: 100 },
    { field: 'Passengers', width: 80 },
    { field: 'specPdfPath', headerName: 'Spec', width: 70,
      cellRenderer: (p) => p.value ? <span className="text-blue-600">PDF</span> : '' },
    { field: 'changeHistory', headerName: 'Changes', width: 90,
      valueGetter: (p) => (p.data.changeHistory || []).length, cellRenderer: CHANGE_RENDERER },
    { field: 'lastUpdatedAt', headerName: 'Last Updated', width: 160, valueFormatter: DATE_FORMATTER },
    { field: 'firstSeenAt', headerName: 'First Seen', width: 160, valueFormatter: DATE_FORMATTER },
    { field: 'Notes', width: 300, tooltipField: 'Notes' },
  ], []);

  const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true }), []);

  const runsColDefs = useMemo(() => [
    { field: 'createdAt', headerName: 'Date', width: 180, valueFormatter: DATE_FORMATTER, sort: 'desc' },
    { field: 'type', headerName: 'Type', width: 100,
      valueGetter: (p) => p.data.type === 'on_market' ? 'On Market' : 'Sales' },
    { field: 'trigger', headerName: 'Trigger', width: 100 },
    { headerName: 'Initiated By', width: 200, valueGetter: (p) => p.data.initiatedBy?.email || '\u2014' },
    { field: 'makeModelNames', headerName: 'Models', width: 250,
      valueFormatter: (p) => (p.value || []).join(', ') },
    { field: 'period', headerName: 'Period (mo)', width: 110 },
    { field: 'status', headerName: 'Status', width: 100,
      cellRenderer: (p) => {
        const color = p.value === 'success' ? 'green' : p.value === 'error' ? 'red' : 'amber';
        return <Chip size="sm" color={color} value={p.value || '\u2014'} />;
      } },
    { headerName: 'New', width: 70, valueGetter: (p) => p.data.counts?.new ?? '\u2014' },
    { headerName: 'Updated', width: 85, valueGetter: (p) => p.data.counts?.updated ?? '\u2014' },
    { headerName: 'Unchanged', width: 100, valueGetter: (p) => p.data.counts?.unchanged ?? '\u2014' },
    { headerName: 'Errors', width: 80, valueGetter: (p) => p.data.counts?.errors ?? '\u2014' },
    { field: 'completedAt', headerName: 'Completed', width: 180, valueFormatter: DATE_FORMATTER },
  ], []);

  const periodOptions = [
    { value: 6, label: 'Last 6 months' },
    { value: 12, label: 'Last 12 months' },
  ];

  const stateOptions = [
    { value: 'for_sale', label: 'For Sale' },
    { value: 'lease', label: 'Lease' },
    { value: 'fractional', label: 'Fractional' },
  ];

  // --- schedule state ---
  const [scheduleConfig, setScheduleConfig] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const loadScheduleConfig = useCallback(async () => {
    setScheduleLoading(true);
    try {
      const res = await funcGetScheduleConfig();
      if (res.data.success) setScheduleConfig(res.data.config);
    } catch (err) {
      console.error('Failed to load schedule config:', err);
    } finally {
      setScheduleLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'schedule') loadScheduleConfig();
  }, [activeTab, loadScheduleConfig]);

  const handleScheduleToggleModel = (modelId) => {
    setScheduleConfig((prev) => {
      const models = (prev?.models || []).map((m) =>
        m.id === modelId ? { ...m, enabled: !m.enabled } : m
      );
      return { ...prev, models };
    });
  };

  const handleScheduleAddModel = (modelOption) => {
    setScheduleConfig((prev) => {
      const existing = (prev?.models || []).find((m) => m.id === modelOption.value);
      if (existing) return prev;
      const models = [...(prev?.models || []), { id: modelOption.value, name: modelOption.label, enabled: true }];
      return { ...prev, models };
    });
  };

  const handleScheduleRemoveModel = (modelId) => {
    setScheduleConfig((prev) => ({
      ...prev,
      models: (prev?.models || []).filter((m) => m.id !== modelId),
    }));
  };

  const handleSaveSchedule = async () => {
    setScheduleSaving(true);
    try {
      await funcSaveScheduleConfig({
        models: scheduleConfig?.models || [],
        enabled: scheduleConfig?.enabled !== false,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setScheduleSaving(false);
    }
  };

  const tabs = [
    { id: 'scrape', label: 'Scrape' },
    { id: 'data', label: mode === 'sales' ? 'Sales Data' : 'On Market Data' },
    { id: 'runs', label: 'Scrape History' },
    { id: 'schedule', label: 'Schedule' },
  ];

  const currentData = mode === 'sales' ? sales : onMarketListings;
  const currentLoading = mode === 'sales' ? salesLoading : onMarketLoading;
  const currentColDefs = mode === 'sales' ? salesColDefs : onMarketColDefs;
  const currentLoadFn = mode === 'sales' ? loadSales : loadOnMarket;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-4">
        <Typography variant="h4">AircraftPost</Typography>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden">
          <button
            onClick={() => setMode('sales')}
            className={`px-4 py-1.5 text-sm font-medium ${
              mode === 'sales' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Sales
          </button>
          <button
            onClick={() => setMode('on_market')}
            className={`px-4 py-1.5 text-sm font-medium ${
              mode === 'on_market' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            On Market
          </button>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t ${
              activeTab === t.id
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <Alert color="red" onClose={() => setError(null)}>{error}</Alert>
      )}

      {/* ============ SCRAPE TAB ============ */}
      {activeTab === 'scrape' && (
        <Card>
          <CardBody className="space-y-4">
            <Typography variant="h6">
              {mode === 'sales' ? 'Scrape Recent Sales' : 'Scrape On Market Listings'}
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Make/Model(s)</label>
                {modelsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Spinner className="h-4 w-4" /> Loading models...
                  </div>
                ) : (
                  <Select
                    isMulti
                    options={modelOptions}
                    value={selectedModels}
                    onChange={setSelectedModels}
                    placeholder="Select make/models to scrape..."
                    maxMenuHeight={300}
                  />
                )}
              </div>
              <div>
                {mode === 'sales' ? (
                  <>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Period</label>
                    <Select
                      options={periodOptions}
                      value={periodOptions.find((p) => p.value === period)}
                      onChange={(opt) => setPeriod(opt.value)}
                    />
                  </>
                ) : (
                  <>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">State</label>
                    <Select
                      options={stateOptions}
                      value={stateOptions.find((s) => s.value === onMarketState)}
                      onChange={(opt) => setOnMarketState(opt.value)}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Button
                color="blue"
                onClick={handleScrape}
                disabled={scraping || selectedModels.length === 0}
                className="flex items-center gap-2"
              >
                {scraping && <Spinner className="h-4 w-4" />}
                {scraping ? 'Scraping...' : 'Scrape Now'}
              </Button>
              {selectedModels.length > 10 && (
                <span className="text-sm text-red-500">Max 10 models per scrape</span>
              )}
            </div>

            {scrapeResult && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg space-y-2">
                <Typography variant="h6" color="green">Scrape Complete</Typography>
                <div className="flex gap-4 text-sm">
                  <span><strong>New:</strong> {scrapeResult.counts?.new || 0}</span>
                  <span><strong>Updated:</strong> {scrapeResult.counts?.updated || 0}</span>
                  <span><strong>Unchanged:</strong> {scrapeResult.counts?.unchanged || 0}</span>
                  <span><strong>Errors:</strong> {scrapeResult.counts?.errors || 0}</span>
                </div>
                {scrapeResult.results?.map((r) => (
                  <div key={r.makeModelId} className="text-sm">
                    {r.makeModel}: {r.aircraftCount} aircraft
                    {r.error && <span className="text-red-500 ml-2">Error: {r.error}</span>}
                    {r.summary && mode === 'sales' && (
                      <span className="text-gray-500 ml-2">(Avg Sale: {r.summary.avgSalePrice || 'N/A'})</span>
                    )}
                    {r.summary && mode === 'on_market' && (
                      <span className="text-gray-500 ml-2">
                        (On Market: {r.summary.aircraftOnMarket || 'N/A'}, Absorption: {r.summary.absorptionRateMonths || 'N/A'} mo)
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* ============ DATA TAB ============ */}
      {activeTab === 'data' && (
        <Card>
          <CardBody className="space-y-4">
            <div className="flex items-center justify-between">
              <Typography variant="h6">
                {mode === 'sales' ? 'Sales' : 'On Market'} Records ({currentData.length})
              </Typography>
              <div className="flex items-center gap-3">
                <div className="w-64">
                  <Select
                    isClearable
                    options={modelOptions}
                    value={filterModel}
                    onChange={setFilterModel}
                    placeholder="Filter by model..."
                  />
                </div>
                <Button size="sm" variant="outlined" onClick={currentLoadFn}>
                  {currentLoading ? <Spinner className="h-4 w-4" /> : 'Refresh'}
                </Button>
              </div>
            </div>
            <div className="ag-theme-quartz" style={{ height: 600 }}>
              {currentLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner className="h-8 w-8" />
                </div>
              ) : (
                <AgGridReact
                  rowData={currentData}
                  columnDefs={currentColDefs}
                  defaultColDef={defaultColDef}
                  animateRows
                  enableCellTextSelection
                  tooltipShowDelay={300}
                />
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* ============ RUNS TAB ============ */}
      {activeTab === 'runs' && (
        <Card>
          <CardBody className="space-y-4">
            <div className="flex items-center justify-between">
              <Typography variant="h6">Scrape Run History</Typography>
              <Button size="sm" variant="outlined" onClick={loadRuns}>
                {runsLoading ? <Spinner className="h-4 w-4" /> : 'Refresh'}
              </Button>
            </div>
            <div className="ag-theme-quartz" style={{ height: 400 }}>
              {runsLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner className="h-8 w-8" />
                </div>
              ) : (
                <AgGridReact
                  rowData={runs}
                  columnDefs={runsColDefs}
                  defaultColDef={defaultColDef}
                  animateRows
                  enableCellTextSelection
                />
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* ============ SCHEDULE TAB ============ */}
      {activeTab === 'schedule' && (
        <Card>
          <CardBody className="space-y-4">
            {scheduleLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner className="h-8 w-8" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Typography variant="h6">Scheduled Scraping</Typography>
                    <Switch
                      checked={scheduleConfig?.enabled !== false}
                      onChange={() => setScheduleConfig((prev) => ({ ...prev, enabled: !(prev?.enabled !== false) }))}
                      label={scheduleConfig?.enabled !== false ? 'Enabled' : 'Disabled'}
                      color="blue"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outlined" onClick={loadScheduleConfig}>
                      Reload
                    </Button>
                    <Button size="sm" color="blue" onClick={handleSaveSchedule} disabled={scheduleSaving}>
                      {scheduleSaving ? <Spinner className="h-4 w-4" /> : 'Save'}
                    </Button>
                  </div>
                </div>

                <Typography variant="small" color="gray">
                  Models are automatically spread across the week. Each model is scraped once per week (both sales and on-market) at randomized times.
                </Typography>

                {scheduleConfig?.lastRunAt && (
                  <div className="text-xs text-gray-500">
                    Last run: {new Date(scheduleConfig.lastRunAt._seconds ? scheduleConfig.lastRunAt._seconds * 1000 : scheduleConfig.lastRunAt).toLocaleString()}
                    {scheduleConfig.lastRunModels && ` — ${scheduleConfig.lastRunModels.join(', ')}`}
                  </div>
                )}

                {/* Add model */}
                <div className="flex items-center gap-3">
                  <div className="w-80">
                    <Select
                      options={modelOptions}
                      onChange={(opt) => { if (opt) handleScheduleAddModel(opt); }}
                      placeholder="Add model to schedule..."
                      value={null}
                      maxMenuHeight={200}
                    />
                  </div>
                </div>

                {/* Model list */}
                <div className="border rounded-lg divide-y">
                  {(scheduleConfig?.models || []).length === 0 ? (
                    <div className="p-4 text-sm text-gray-400 text-center">No models configured</div>
                  ) : (
                    (scheduleConfig?.models || [])
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((m) => (
                        <div key={m.id} className="flex items-center justify-between px-4 py-2">
                          <div className="flex items-center gap-3">
                            <Switch
                              checked={m.enabled}
                              onChange={() => handleScheduleToggleModel(m.id)}
                              color="blue"
                            />
                            <span className={`text-sm ${m.enabled ? 'text-gray-800' : 'text-gray-400'}`}>
                              {m.name}
                            </span>
                          </div>
                          <button
                            onClick={() => handleScheduleRemoveModel(m.id)}
                            className="text-xs text-red-400 hover:text-red-600"
                          >
                            Remove
                          </button>
                        </div>
                      ))
                  )}
                </div>

                <div className="text-xs text-gray-400">
                  {(scheduleConfig?.models || []).filter((m) => m.enabled).length} of{' '}
                  {(scheduleConfig?.models || []).length} models enabled
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
